import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

/**
 * Brand pills use the portal's emphasis pair; status pills keep semantic tones.
 * Optional dots make the status easier to scan in dense lists and tables.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-emphasis text-emphasis-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        outline: 'border border-border text-muted-foreground',
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/12 text-warning',
        destructive: 'bg-destructive/10 text-destructive',
        info: 'bg-info/10 text-info',
        muted: 'bg-muted text-muted-foreground',
        pending: 'bg-pending/10 text-pending',
        active: 'bg-active/12 text-active',
        hold: 'bg-hold/12 text-hold',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

/** Dot colour per variant — solid where the fill is tinted, quiet otherwise. */
const dotByVariant: Record<string, string> = {
  default: 'bg-emphasis-foreground',
  secondary: 'bg-secondary-foreground/50',
  outline: 'bg-muted-foreground/60',
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  info: 'bg-info',
  muted: 'bg-muted-foreground/60',
  pending: 'bg-pending',
  active: 'bg-active',
  hold: 'bg-hold',
};

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Show the leading status dot. Off by default. */
  dot?: boolean;
}

export function Badge({ className, variant, dot = false, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', dotByVariant[variant ?? 'default'])}
          aria-hidden
        />
      )}
      {children}
    </span>
  );
}

export { badgeVariants };
